/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Box, Heading, VStack } from 'chakra-v3';
import { Text } from './Text';

export interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

/**
 * FeatureCard Component
 *
 * Displays a feature with an icon, title, and description.
 * Used in the Foundation Features section.
 *
 * @example
 * ```tsx
 * <FeatureCard
 *   icon={<Icon as={BsPersonCheck} boxSize="40px" color="text.teal" />}
 *   title="Verified raters"
 *   description="Every participant is identity-verified..."
 * />
 * ```
 */
export const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => {
  return (
    <VStack
      bg="transparent"
      px={{ base: 4, md: '24px' }}
      py={{ base: 6, md: '32px' }}
      gap={{ base: 4, md: '24px' }}
      alignItems="flex-start"
      borderRadius="0"
    >
      <Box color="text.teal" w="40px" h="40px">
        {icon}
      </Box>
      <VStack gap={{ base: 2, md: '12px' }} alignItems="flex-start" w="100%">
        <Heading
          as="h3"
          fontSize={{ base: 'lg', md: '24px' }}
          fontWeight="500"
          color="text.teal"
          fontFamily="'Plus Jakarta Sans', sans-serif"
          lineHeight="1.4"
          letterSpacing="0.06px"
        >
          {title}
        </Heading>
        <Text color="text.darkTeal" size={{ base: 'sm', md: 'md' }}>
          {description}
        </Text>
      </VStack>
    </VStack>
  );
};
